import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";
import DigestEmailPreview from "@/components/DigestEmailPreview";
import {
  getDigestPreferences,
  updateDigestPreferences,
  previewDigest,
  digestHistory,
  sendTestDigest,
  runScheduler,
  getIntegrationStatus,
} from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Play, Send, Clock3, MailCheck } from "lucide-react";

const DAYS = [
  { v: "monday", label: "Monday" },
  { v: "tuesday", label: "Tuesday" },
  { v: "wednesday", label: "Wednesday" },
  { v: "thursday", label: "Thursday" },
  { v: "friday", label: "Friday" },
  { v: "saturday", label: "Saturday" },
  { v: "sunday", label: "Sunday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  v: i,
  label: `${i.toString().padStart(2, "0")}:00`,
}));

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Australia/Sydney",
  "UTC",
];

function StatusPill({ status }) {
  const map = {
    generated: { c: "#C7CBD1", label: "Generated" },
    preview: { c: "#6CC24A", label: "Preview" },
    sent: { c: "#6CC24A", label: "Sent" },
    skipped: { c: "#EAB308", label: "Skipped" },
    pending_provider: { c: "#EAB308", label: "Pending provider" },
    failed: { c: "#EF4444", label: "Failed" },
  };
  const meta = map[status] || map.generated;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border"
      style={{ color: meta.c, borderColor: `${meta.c}55`, background: `${meta.c}0F` }}
    >
      {meta.label}
    </span>
  );
}

export default function Digest() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [provider, setProvider] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [digest, setDigest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, hist, is] = await Promise.all([
          getDigestPreferences(),
          digestHistory(),
          getIntegrationStatus(),
        ]);
        setPrefs(p.preferences);
        setProvider(p.email_provider);
        setHistory(hist);
        setIntegrationStatus(is);
        // Try to auto-load a preview if there's an assessment
        try {
          const preview = await previewDigest();
          setDigest(preview);
        } catch {
          setDigest(null);
        }
      } catch (e) {
        toast.error("Could not load digest settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const emailEnabled = provider?.resend_configured && provider?.delivery_enabled;

  const handleChange = async (patch) => {
    if (!prefs) return;
    setSaving(true);
    const optimistic = { ...prefs, ...patch };
    setPrefs(optimistic);
    try {
      const r = await updateDigestPreferences(patch);
      setPrefs(r.preferences);
    } catch (e) {
      toast.error("Could not save preferences");
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const d = await previewDigest();
      setDigest(d);
      toast.success("Preview generated");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Preview failed";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleSendTest = async () => {
    try {
      await sendTestDigest();
      toast.success("Test digest sent");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Email delivery is not enabled yet.";
      toast.info(detail);
    }
  };

  const handleRunScheduler = async () => {
    try {
      const r = await runScheduler();
      toast.success(`Scheduler pass complete — ${r.count} digest${r.count === 1 ? "" : "s"} generated`);
      const hist = await digestHistory();
      setHistory(hist);
    } catch (e) {
      toast.error("Scheduler run failed");
    }
  };

  const nextRun = useMemo(() => {
    if (!prefs) return "";
    return `${prefs.day.charAt(0).toUpperCase() + prefs.day.slice(1)} at ${prefs.hour
      .toString()
      .padStart(2, "0")}:00 ${prefs.timezone}`;
  }, [prefs]);

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <NavBar />
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="pp-label mb-2">Weekly Trust Digest</div>
            <h1 className="font-display text-3xl md:text-4xl text-white leading-tight">
              A calm weekly read on where your trust score is heading.
            </h1>
            <p className="mt-3 text-sm text-[#C7CBD1] max-w-2xl leading-relaxed">
              The digest summarizes your current Trust Score, what moved since
              last week, completed improvements, new leaks detected, and the
              recommended priorities for the next seven days.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="pp-label" data-testid="digest-loading">
            Loading digest settings…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
            {/* Preferences column */}
            <div className="space-y-6">
              {/* Delivery status card */}
              <div className="pp-card p-5" data-testid="digest-delivery-status">
                <div className="flex items-center gap-2 mb-2">
                  <MailCheck className="h-4 w-4 text-[#EAB308]" />
                  <div className="pp-label">Email delivery</div>
                </div>
                <div className="font-display text-lg text-white leading-snug">
                  {emailEnabled
                    ? "Automated Monday delivery is live."
                    : "Automated delivery — Coming Soon"}
                </div>
                <p className="text-sm text-[#C7CBD1] mt-2 leading-relaxed">
                  {emailEnabled
                    ? "Your digest will land in the inbox at the time you choose below."
                    : "The digest system, scheduling, previews and history are fully live. Actual email sending activates once RESEND_API_KEY is configured."}
                </p>
              </div>

              {/* Preferences card */}
              <div className="pp-card p-5">
                <div className="pp-label mb-4">Delivery preferences</div>
                <div className="flex items-center justify-between py-2 border-b border-[#333C47]">
                  <div>
                    <div className="text-sm text-white">Digest enabled</div>
                    <div className="text-xs text-[#C7CBD1]">
                      Turn the weekly digest on or off.
                    </div>
                  </div>
                  <button
                    onClick={() => handleChange({ enabled: !prefs.enabled })}
                    data-testid="pref-toggle-enabled"
                    className={`w-10 h-5 border ${
                      prefs.enabled
                        ? "bg-[#6CC24A] border-[#6CC24A]"
                        : "bg-[#0F1115] border-[#333C47]"
                    } relative`}
                    role="switch"
                    aria-checked={prefs.enabled}
                  >
                    <span
                      className={`absolute top-[1px] w-[16px] h-[16px] transition-all ${
                        prefs.enabled ? "left-[22px] bg-[#0F1115]" : "left-[1px] bg-white"
                      }`}
                    />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4">
                  <div>
                    <label className="pp-label block mb-2">Day</label>
                    <Select
                      value={prefs.day}
                      onValueChange={(v) => handleChange({ day: v })}
                    >
                      <SelectTrigger
                        data-testid="pref-day"
                        className="bg-[#0F1115] border border-[#333C47] rounded-none h-auto py-2.5 text-white focus:ring-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1F24] border border-[#333C47] rounded-none">
                        {DAYS.map((d) => (
                          <SelectItem
                            key={d.v}
                            value={d.v}
                            className="rounded-none focus:bg-[#232931] focus:text-white text-white"
                          >
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="pp-label block mb-2">Time</label>
                    <Select
                      value={String(prefs.hour)}
                      onValueChange={(v) => handleChange({ hour: parseInt(v, 10) })}
                    >
                      <SelectTrigger
                        data-testid="pref-hour"
                        className="bg-[#0F1115] border border-[#333C47] rounded-none h-auto py-2.5 text-white focus:ring-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1F24] border border-[#333C47] rounded-none max-h-72">
                        {HOURS.map((h) => (
                          <SelectItem
                            key={h.v}
                            value={String(h.v)}
                            className="rounded-none focus:bg-[#232931] focus:text-white text-white"
                          >
                            {h.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="pp-label block mb-2">Time zone</label>
                  <Select
                    value={prefs.timezone}
                    onValueChange={(v) => handleChange({ timezone: v })}
                  >
                    <SelectTrigger
                      data-testid="pref-tz"
                      className="bg-[#0F1115] border border-[#333C47] rounded-none h-auto py-2.5 text-white focus:ring-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1F24] border border-[#333C47] rounded-none max-h-72">
                      {TIMEZONES.map((tz) => (
                        <SelectItem
                          key={tz}
                          value={tz}
                          className="rounded-none focus:bg-[#232931] focus:text-white text-white"
                        >
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-3">
                  <label className="pp-label block mb-2">Recipient email</label>
                  <input
                    type="email"
                    value={prefs.recipient_email || ""}
                    onChange={(e) => setPrefs({ ...prefs, recipient_email: e.target.value })}
                    onBlur={(e) => handleChange({ recipient_email: e.target.value || null })}
                    data-testid="pref-recipient"
                    placeholder="you@example.com"
                    className="w-full bg-[#0F1115] border border-[#333C47] px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#6CC24A]"
                  />
                </div>

                <div className="mt-4 flex items-center justify-between py-2 border-t border-[#333C47] pt-4">
                  <div>
                    <div className="text-sm text-white">Send when nothing changed</div>
                    <div className="text-xs text-[#C7CBD1]">
                      Skip weeks with no meaningful movement.
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      handleChange({ send_when_no_changes: !prefs.send_when_no_changes })
                    }
                    data-testid="pref-toggle-when-no-changes"
                    className={`w-10 h-5 border ${
                      prefs.send_when_no_changes
                        ? "bg-[#6CC24A] border-[#6CC24A]"
                        : "bg-[#0F1115] border-[#333C47]"
                    } relative`}
                    role="switch"
                    aria-checked={prefs.send_when_no_changes}
                  >
                    <span
                      className={`absolute top-[1px] w-[16px] h-[16px] transition-all ${
                        prefs.send_when_no_changes
                          ? "left-[22px] bg-[#0F1115]"
                          : "left-[1px] bg-white"
                      }`}
                    />
                  </button>
                </div>

                <div className="mt-5 pt-4 border-t border-[#333C47] flex items-center gap-2 text-xs text-[#C7CBD1]">
                  <Clock3 className="h-3.5 w-3.5" />
                  Next scheduled digest: <span className="text-white">{nextRun}</span>
                </div>
              </div>

              {/* Actions card */}
              <div className="pp-card p-5">
                <div className="pp-label mb-3">Actions</div>
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="digest-generate-preview"
                  className="pp-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" /> Generate preview now
                    </>
                  )}
                </button>
                <button
                  onClick={handleSendTest}
                  disabled={!emailEnabled}
                  data-testid="digest-send-test"
                  className="mt-3 w-full pp-btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    emailEnabled
                      ? "Send this digest to your recipient email"
                      : "Send Test is disabled until an email provider is configured"
                  }
                >
                  <Send className="h-4 w-4" /> Send test email
                </button>
                {!emailEnabled && (
                  <div className="mt-2 text-[11px] text-[#C7CBD1]">
                    Disabled until Resend is configured.
                  </div>
                )}
                <button
                  onClick={handleRunScheduler}
                  data-testid="digest-run-scheduler"
                  className="mt-3 w-full pp-btn-secondary flex items-center justify-center gap-2 text-xs"
                >
                  Run scheduler pass now
                </button>
              </div>
            </div>

            {/* Preview column */}
            <div className="space-y-6">
              <DigestEmailPreview digest={digest} />

              <div className="pp-surface">
                <div className="px-5 py-4 border-b border-[#333C47]">
                  <div className="pp-label mb-1">Digest history</div>
                  <div className="font-display text-white">
                    Every digest we've generated for you.
                  </div>
                </div>
                <div className="divide-y divide-[#333C47]" data-testid="digest-history">
                  {history.length === 0 && (
                    <div className="p-6 text-sm text-[#C7CBD1]">
                      No digests yet. Preview one or trigger the scheduler.
                    </div>
                  )}
                  {history.map((d) => (
                    <div
                      key={d.digest_id}
                      className="p-5 flex items-center justify-between gap-4 hover:bg-[#232931]"
                      data-testid={`digest-history-item-${d.digest_id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{d.subject}</div>
                        <div className="text-xs text-[#C7CBD1] mt-0.5">
                          {new Date(d.generated_at).toLocaleString()} · {d.week_key}
                          {d.skip_reason ? ` · ${d.skip_reason}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="pp-label text-[10px]">Score</div>
                          <div className="metric text-lg font-bold text-white">
                            {d.score_at_generation}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="pp-label text-[10px]">Δ</div>
                          <div
                            className="metric text-lg font-bold"
                            style={{
                              color:
                                d.score_change > 0
                                  ? "#6CC24A"
                                  : d.score_change < 0
                                    ? "#EF4444"
                                    : "#C7CBD1",
                            }}
                          >
                            {d.score_change > 0 ? "+" : ""}
                            {d.score_change}
                          </div>
                        </div>
                        <StatusPill status={d.delivery_status} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
